import { useMemo } from 'react'
import { get } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
  EventMapGenerator,
  generateEventMap,
  GenericEventMap,
  SiteActivityConfig,
  SpecificEventMap
} from 'events/lib'
import { SiteActivityConfigFn } from 'events/lib/baseGenerator'

import { usePageLoaded } from 'context/pageLoaded'

type UseEventOpts<EMap extends Record<string, GenericEventMap>> = {
  generatorFn?: EventMapGenerator<EMap>
}

export function useEvent<
  T extends keyof EMap,
  EMap extends Record<string, GenericEventMap> = ReturnType<
    typeof generateEventMap
  >
> (ns?: T, args?: UseEventOpts<EMap>) {
  const {
    generatorFn = generateEventMap as unknown as EventMapGenerator<EMap>
  } = args || {}
  const { i18n } = useTranslation()

  const eventMap = useMemo(() => generatorFn({ i18n }), [generatorFn, i18n])

  const { loaded, addEvent } = usePageLoaded()

  if (!ns) {
    return {
      eventMap: {},
      fireEvent: () => {}
    }
  }

  const fireEvent = <K extends keyof EMap[T]>(key: K, params?: EMap[T][K] extends (args: infer P) => SiteActivityConfig ? P : undefined) => {
    if (key) {
      let cfg: SiteActivityConfig | SiteActivityConfigFn = get(eventMap[ns], key)
      if (typeof cfg === 'function') {
        cfg = cfg(params)
      }
      if (loaded) {
        window.globalHeaderEvents?.handleEvent(cfg)
      } else {
        addEvent(cfg)
      }
    }
  }

  return { eventMap: eventMap[ns], fireEvent }
}

export interface UseEventProps<T extends keyof SpecificEventMap> {
  eventNamespace: T
  elementId: keyof SpecificEventMap[T]
}
